import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";import { useDepartment } from "../../contexts/DepartmentMasterContext";
import Modal from "../../components/UI/Modal";
import DepartmentForm from "../../components/Form/DepartmentForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import Skeleton from "react-loading-skeleton";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteDepartment }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteDepartment(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const DepartmentMasters = React.memo(() => {
  const { departments, addDepartment, updateDepartment, deleteDepartment, isLoading } = useDepartment();
  
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingDepartment(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((candidate) => {
    setEditingDepartment(candidate);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingDepartment(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (department) => {
      setLoading(true);
      try {
        if (editingDepartment) {
          // console.log("department payload", department);
        
          // Convert FormData to object
          // console.log(department instanceof FormData)
          const departmentData = department instanceof FormData ? convertFormDataToObject(department) : department;
  
          // console.log("Payload being sent:", { ...editingDepartment, ...departmentData });
  
          await updateDepartment({ ...editingDepartment, ...departmentData });
          setMessage("Department updated successfully.");
          // setNotification("Department updated successfully.");
          setNotification({
            message: "Department updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create department", department)
          await addDepartment(department);
          setMessage("Department added successfully.");
          // setNotification("Department added successfully.");
          setNotification({
            message: "Department added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: `Unexpected error during submission:, ${err}`,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingDepartment, addDepartment, updateDepartment, closeModal]
  );

  // Memoize campaigns for dropdown options
//   const departmentOptions = useMemo(
//     () => departments.map((c) => ({ value: c.id, label: c.name })),
//     [departments]
//   );

  // Memoize candidates with campaign names for the table
  const departmentForTable = useMemo(
    () =>
      departments.map((department) => ({
        ...department,
        name: department?.name || "Unknown Department",
      })),
    [departments]
  );

  const handleDeleteDepartment = async (id) => {
    try {
      await deleteDepartment(id);
      // setNotification("Department deleted successfully");
      setNotification({
        message: "Department deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete department");
      setNotification({
        message: "Failed to delete department",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteDepartment={handleDeleteDepartment}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteDepartment,
    ]
  );

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Department Masters</h2>
        <div className="flex gap-2"> 
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add Department
          </button>
        </div> 
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Skeleton Loader */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      ) : (
        <div>
          <ReusableTable
            data={departmentForTable}
            // data={requests.slice().reverse()}
            loading={isLoading}
            columns={columns}
            filterable={true}
            excludeFilterColumns={["actions"]}
            // excludeFilterColumns={excludeFilterColumns}
          />
        </div>
      )}

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingDepartment ? "Edit Department" : "Add Department"}
      >
        <DepartmentForm
          loading={loading} 
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingDepartment || { name: "", description: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default DepartmentMasters;
